import React, { useCallback } from 'react';
import Typography from 'carbon-react/lib/components/typography';
import Box from 'carbon-react/lib/components/box';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { ListingPageHelpLink } from '@futrli/ui-components/components/listing-page/listing-page-help-link/listing-page-help-link';

import { VirtualizedCardList } from '../../../ui/virtualized-card-list/virtualized-card-list';
import { VirtualizedCardListEmptyState } from '../../../ui/virtualized-card-list/virtualized-card-list-empty-state';
import { FiltersSidebar } from '../../../ui/filters-sidebar/filters-sidebar';
import {
  CustomersAndSuppliersContextContainer,
  useCustomersAndSuppliersContext,
  TransformedContact
} from './context/customers-and-suppliers-context';
import { FilterHeader } from './components/filter-header/filter-header';
import { CustomerSupplierRow } from './components/customer-supplier-card/customer-supplier-row';
import { CustomersSuppliersSidePanel } from '../../../forecast/detail/performance/customers-and-suppliers/components/customers-suppliers-side-panel';

const CARD_ROW_HEIGHT = 78; // 70px card + 8px margin-bottom gap

const CustomersAndSuppliersTabInner = () => {
  const intl = useIntl();

  const {
    displayedData,
    isLoading,
    isFiltersSidebarOpen,
    applyDraftFilters,
    cancelDraftFilters,
    filterConfig,
    handlers,
    isSidePanelOpen,
    activeContact,
    isSidePanelLoading,
    closeSidePanel
  } = useCustomersAndSuppliersContext();

  const renderRow = useCallback((item: TransformedContact) => <CustomerSupplierRow item={item} />, []);

  return (
    <Box>
      <Box display="flex" gap="16px" alignItems="center" mt="32px" mb="16px">
        <Typography variant="h1" fontSize="20px" fontWeight="500" mb={0}>
          <FormattedMessage id="predict.customers_and_suppliers" defaultMessage="Customers & Suppliers" />
        </Typography>
        <ListingPageHelpLink url="https://support.futrli.com/en/articles/14301256-customers-suppliers" />
      </Box>
      <FilterHeader />
      <VirtualizedCardList<TransformedContact>
        data={displayedData}
        renderRow={renderRow}
        rowHeight={CARD_ROW_HEIGHT}
        isLoading={isLoading}
        emptyState={
          <VirtualizedCardListEmptyState
            title={intl.formatMessage({
              id: 'overview.empty_customers_suppliers_title',
              defaultMessage: 'No customers or suppliers to show'
            })}
            action={
              <ListingPageHelpLink
                url="https://support.futrli.com/en/articles/14301256-customers-suppliers"
                title={intl.formatMessage({
                  id: 'overview.empty_customers_suppliers_help',
                  defaultMessage: 'Learn about customers and suppliers (opens in new tab)'
                })}
              />
            }
          />
        }
      />
      <FiltersSidebar
        isOpen={isFiltersSidebarOpen}
        onClose={cancelDraftFilters}
        onApply={applyDraftFilters}
        handlers={handlers}
        filterConfig={filterConfig}
        applyLabel={intl.formatMessage({ id: 'common.apply', defaultMessage: 'Apply' })}
        cancelLabel={intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
      />
      <CustomersSuppliersSidePanel
        activeCustomerSupplier={activeContact}
        isOpen={isSidePanelOpen}
        isLoading={isSidePanelLoading}
        onClose={closeSidePanel}
      />
    </Box>
  );
};

export const CustomersAndSuppliersTab = () => {
  return (
    <CustomersAndSuppliersContextContainer>
      <CustomersAndSuppliersTabInner />
    </CustomersAndSuppliersContextContainer>
  );
};
